import React, { useContext, useEffect, useState } from 'react';
import { Dimmer, Loader } from 'semantic-ui-react';
import * as Authentication from '../../../services/Authentication';
import UserContext from '../../../contexts/UserContext';
import sendToast from '../../../utils/Toast';
import {
    completeGuestOnboarding,
    fetchPublicResidentialFacilities,
    GuestOnboardingPayload,
    ResidentialFacilityListItem,
} from '../../services/Identity';
import { neighborsLogoPath } from '../../../utils/logoPaths';
import { resolveGuestLandingPath } from '../../utils/guestAccessRouting';
import './style.less';

interface CognitoIdentityLink {
    providerName?: string;
    provider?: string;
}

interface CognitoAttributes {
    identities?: string | CognitoIdentityLink[];
    email?: string;
}

interface CognitoUserLike {
    attributes?: CognitoAttributes;
}

const resolveGuestLoginMethod = (cognitoUser?: CognitoUserLike): string => {
    const attributes = cognitoUser && cognitoUser.attributes ? cognitoUser.attributes : {};
    const identitiesRaw = attributes.identities;

    if (identitiesRaw) {
        try {
            const identities = typeof identitiesRaw === 'string' ? JSON.parse(identitiesRaw) : identitiesRaw;
            const firstIdentity = Array.isArray(identities) ? identities[0] : null;
            const providerName = String(
                firstIdentity && (firstIdentity.providerName || firstIdentity.provider)
                    ? (firstIdentity.providerName || firstIdentity.provider)
                    : '',
            );

            if (providerName === 'Google') return 'google';
            if (providerName === 'LoginWithAmazon') return 'amazon';
            if (providerName === 'SignInWithApple') return 'apple';
            if (providerName === 'Facebook') return 'facebook';
        } catch (_error) {
            // Fall through to non-social defaults.
        }
    }

    if (attributes.email) return 'email';
    return 'cognito';
};

const extractSocialAttrs = (cognitoUser: any): { given_name: string; family_name: string; email: string } => {
    if (!cognitoUser) return { given_name: '', family_name: '', email: '' };
    const attrs = cognitoUser.attributes || {};
    const payload = cognitoUser?.signInUserSession?.idToken?.payload || {};
    const fullName: string = attrs.name || payload.name || '';
    const [namePart0 = '', namePart1 = ''] = fullName.split(' ');
    return {
        given_name: attrs.given_name || payload.given_name || namePart0,
        family_name: attrs.family_name || payload.family_name || namePart1,
        email: attrs.email || payload.email || attrs['custom:email'] || payload['custom:email'] || '',
    };
};

const GuestCompleteProfile = ({ history }) => {
    const { setUser, userObject } = useContext(UserContext);
    const [facilities, setFacilities] = useState<ResidentialFacilityListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [step, setStep] = useState<1 | 2>(1);
    const initialAttrs = extractSocialAttrs(userObject?.cognitoUser);
    const [form, setForm] = useState<GuestOnboardingPayload>({
        facilityId: '',
        FirstName: initialAttrs.given_name,
        LastName: initialAttrs.family_name,
        Email: initialAttrs.email,
        Phone: '',
        Address1: '',
        Address2: '',
        City: '',
        State: '',
        ZIP: '',
    });

    // Once Cognito app client has email in Writable attributes, email will appear
    // in the idToken payload and extractSocialAttrs will pick it up via payload.email.
    useEffect(() => {
        if (!userObject?.cognitoUser) return;
        const derived = extractSocialAttrs(userObject.cognitoUser);
        setForm((prev) => ({
            ...prev,
            FirstName: prev.FirstName || derived.given_name,
            LastName: prev.LastName || derived.family_name,
            Email: prev.Email || derived.email,
        }));
    }, [userObject]);

    useEffect(() => {
        fetchPublicResidentialFacilities()
            .then(setFacilities)
            .catch(() => sendToast('error', 'Unable to load residential facilities'));
    }, []);

    const updateForm = (key: string, value: string) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleContinue = () => {
        if (!form.FirstName.trim() || !form.LastName.trim() || !form.Email.trim()) {
            sendToast('warn', 'First name, last name, and email are required.');
            return;
        }
        setStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBackStep = () => {
        setStep(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBackToWelcome = async (e: React.MouseEvent) => {
        e.preventDefault();
        try {
            await Authentication.cognitoSignOut();
        } catch (_error) {
            // Session may already be invalid - fall through and clear local state regardless.
        }
        setUser({
            cognitoUser: {},
            profile: undefined,
            registrants: { list: [], activeRegistrantsIndexes: [] },
            username: undefined,
            authenticated: false,
            userType: undefined,
            guestAccess: undefined,
            guestIdentity: undefined,
            needsOnboarding: false,
        });
        history.push('/welcome');
    };

    const handleSubmit = async () => {
        const FirstName = form.FirstName.trim();
        const LastName = form.LastName.trim();
        const Email = form.Email.trim();
        if (!form.facilityId || !FirstName || !LastName || !Email) {
            sendToast('warn', 'Facility, first name, last name, and email are required.');
            return;
        }

        setLoading(true);
        try {
            const loginMethod = resolveGuestLoginMethod(userObject?.cognitoUser);
            const identity = await completeGuestOnboarding({
                ...form,
                FirstName,
                LastName,
                Email,
                LoginMethod: loginMethod,
            });

            if (identity.needsOnboarding) {
                sendToast('error', 'We were unable to save your profile. Please try again, or contact the community directly if this keeps happening.');
                return;
            }

            const guestUserObject = {
                ...userObject,
                profile: identity.profile,
                username: identity.profile?.Username || identity.profile?.Email || userObject.username,
                registrants: {
                    list: identity.profile ? [identity.profile] : [],
                    activeRegistrantsIndexes: identity.profile ? [0] : [],
                },
                authenticated: true,
                userType: 'guest',
                guestAccess: identity.access,
                guestIdentity: identity,
                needsOnboarding: identity.needsOnboarding,
            };
            setUser(guestUserObject);
            history.push(resolveGuestLandingPath(identity.access, identity.needsOnboarding));
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Unable to complete profile');
        } finally {
            setLoading(false);
        }
    };

    const facilityOptions = facilities.map((facility) => ({
        value: facility.facilityId,
        label: `${facility.communityName || facility.facilityName}${facility.paymentEnabled ? ' - subscription required' : ''}`,
    }));

    return (
        <div className="guest-profile-page">
            <Dimmer active={loading} inverted>
                <Loader active={loading} />
            </Dimmer>
            <main className="guest-profile-shell">
                <aside className="guest-profile-rail">
                    <div className="guest-profile-logo">
                        <span className="guest-profile-logo-chip">
                            <img src={`${process.env.PUBLIC_URL}${neighborsLogoPath}`} alt="Speak2 Family" />
                        </span>
                    </div>

                    <div className="guest-profile-rail-copy">
                        <div>
                            <h1>Let&apos;s get you started</h1>
                            <p>Share your details and fill out the form, so we can connect you to the right community.</p>
                        </div>
                    </div>
                </aside>

                <section className="guest-profile-form-panel">
                    <div className="guest-profile-eyebrow">Complete your profile</div>
                    <h2>{step === 2 ? 'Find your community' : 'Tell us about you'}</h2>
                    <p className="guest-profile-sub">
                        {step === 2 ? (
                            'Choose a community and tell us a bit about yourself.'
                        ) : (
                            <>Fields marked <span className="guest-profile-req">*</span> are required.</>
                        )}
                    </p>

                    <div className="guest-profile-progress">
                        <div className="guest-profile-progress-top">
                            <span className="guest-profile-progress-step">
                                {step === 2 ? 'Step 2 · Your community' : 'Step 1 · Your details'}
                            </span>
                            <span className="guest-profile-progress-count">{step} of 2</span>
                        </div>
                        <div
                            className="guest-profile-progress-track"
                            role="progressbar"
                            aria-valuemin={1}
                            aria-valuemax={2}
                            aria-valuenow={step}
                        >
                            <div className="guest-profile-progress-fill" style={{ width: step === 2 ? '100%' : '50%' }} />
                        </div>
                    </div>

                    {step === 1 && (
                        <div className="guest-profile-step">
                            <div className="guest-profile-group-label">Your details</div>
                            <div className="guest-profile-grid2">
                                <div className="guest-profile-field">
                                    <label>First name <span className="guest-profile-req">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="Jane"
                                        autoComplete="given-name"
                                        value={form.FirstName}
                                        onChange={(e) => updateForm('FirstName', e.target.value)}
                                    />
                                </div>
                                <div className="guest-profile-field">
                                    <label>Last name <span className="guest-profile-req">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="Doe"
                                        autoComplete="family-name"
                                        value={form.LastName}
                                        onChange={(e) => updateForm('LastName', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="guest-profile-grid2">
                                <div className="guest-profile-field">
                                    <label>Email <span className="guest-profile-req">*</span></label>
                                    <input
                                        type="email"
                                        placeholder="jane@email.com"
                                        autoComplete="email"
                                        value={form.Email}
                                        onChange={(e) => updateForm('Email', e.target.value)}
                                    />
                                </div>
                                <div className="guest-profile-field">
                                    <label>Phone</label>
                                    <input
                                        type="tel"
                                        placeholder="(555) 123-4567"
                                        autoComplete="tel"
                                        value={form.Phone}
                                        onChange={(e) => updateForm('Phone', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="guest-profile-group-label">Address</div>
                            <div className="guest-profile-field">
                                <label>Street address</label>
                                <input
                                    type="text"
                                    placeholder="123 Main St"
                                    autoComplete="address-line1"
                                    value={form.Address1}
                                    onChange={(e) => updateForm('Address1', e.target.value)}
                                />
                            </div>
                            <div className="guest-profile-grid-addr2">
                                <div className="guest-profile-field">
                                    <label>City</label>
                                    <input
                                        type="text"
                                        placeholder="Campbell Hall"
                                        autoComplete="address-level2"
                                        value={form.City}
                                        onChange={(e) => updateForm('City', e.target.value)}
                                    />
                                </div>
                                <div className="guest-profile-field">
                                    <label>State</label>
                                    <input
                                        type="text"
                                        placeholder="NY"
                                        autoComplete="address-level1"
                                        value={form.State}
                                        onChange={(e) => updateForm('State', e.target.value)}
                                    />
                                </div>
                                <div className="guest-profile-field">
                                    <label>ZIP code</label>
                                    <input
                                        type="text"
                                        placeholder="10916"
                                        autoComplete="postal-code"
                                        value={form.ZIP}
                                        onChange={(e) => updateForm('ZIP', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="guest-profile-actions">
                                <a
                                    className="guest-profile-btn-ghost"
                                    href="/welcome"
                                    onClick={handleBackToWelcome}
                                >
                                    Back
                                </a>
                                <button className="guest-profile-btn-primary" type="button" onClick={handleContinue}>
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="guest-profile-step">
                            <div className="guest-profile-group-label">Your community</div>
                            <div className="guest-profile-field">
                                <label>Select a community <span className="guest-profile-req">*</span></label>
                                <select value={form.facilityId} onChange={(e) => updateForm('facilityId', e.target.value)}>
                                    <option value="" disabled>Choose a community…</option>
                                    {facilityOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="guest-profile-actions">
                                <button className="guest-profile-btn-ghost" type="button" onClick={handleBackStep}>
                                    Back
                                </button>
                                <button
                                    className="guest-profile-btn-primary"
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    Send request
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default GuestCompleteProfile;
